import { t } from '@/i18n/i18n'
import {
  type AdImageLinkWithFiles,
  type AdModeType,
  type ApplicationCfgWithAdsFiles,
} from '@/pages/ads/ads.types'
import {
  type AdImageLink,
  type ApplicationConfig,
  ApplicationConfigService,
  type ApplicationFeatures,
} from '@/shared/service/applicationConfig.service'
import { ErrorService } from '@/shared/service/errorService'
import { FilesService } from '@/shared/service/files.service'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'
import { create } from '@/shared/utils/zustandVue'
import { isNotNullish } from '@/utils'

import { InternalType } from '../../shared/types/files.type'

class AdsState {
  applicationCfg: ApplicationCfgWithAdsFiles | undefined = undefined
  loading = false
  isDrawerAdsPercentageValid = false
  isSplashAdsPercentageValid = false
  currentAdIndex: number | undefined = undefined
  adMode: AdModeType | undefined = undefined
  showDeleteDialog = false
  showAdModal = false
  splashAds: AdImageLinkWithFiles[] = []
  drawerAds: AdImageLinkWithFiles[] = []
  splashDuration = 2
  currentAd: AdImageLinkWithFiles | undefined = undefined
}

function toAdImageLinkWithFiles(ad: AdImageLink): AdImageLinkWithFiles {
  return {
    ...ad,
    croppedImage: undefined,
    videoFile: undefined,
  }
}

function toApplicationCfgWithAdsFiles(
  cfg: ApplicationConfig,
): ApplicationCfgWithAdsFiles {
  return {
    ...cfg,
    drawerAds: cfg.drawerAds.map(toAdImageLinkWithFiles),
    splashAds: cfg.splashAds.map(toAdImageLinkWithFiles),
  }
}

function addOrUpdateAd(
  payload: { index: number | undefined; ad: AdImageLinkWithFiles },
  adsArray: AdImageLinkWithFiles[],
): AdImageLinkWithFiles[] {
  if (payload.index != null) {
    const result = [...adsArray]
    result.splice(payload.index, 1, payload.ad)
    return result
  }
  return [...adsArray, payload.ad]
}

function isAdsPercentageValid(
  features: ApplicationFeatures | undefined,
  ads: AdImageLinkWithFiles[],
): boolean {
  if (features?.allowAdsRotation === false) {
    return true
  }
  const total = ads.reduce((acc, ad: AdImageLinkWithFiles) => {
    return acc + ad.percentage
  }, 0)
  return total === 100 || ads.length === 0
}

type AdsStore = AdsState & {
  load: () => void
  unload: () => void
  setAdToDeleteIndex: (payload: { index: number; mode: AdModeType }) => void
  setApplicationCfg: (applicationCfg: ApplicationCfgWithAdsFiles) => void
  setDrawerAdImage: (payload: { index: number; filename: string }) => void
  setSplashAdImage: (payload: {
    index: number
    filename: string | null
  }) => void
  setDrawerAdVideo: (payload: {
    index: number
    filename: string | null
  }) => void
  setSplashAdVideo: (payload: {
    index: number
    filename: string | null
  }) => void
  deleteAd: () => void
  closeDeleteAd: () => void
  addAd: (mode: AdModeType) => void
  closeAdModal: () => void
  saveOrUpdateAd: (payload: {
    index: number | undefined
    ad: AdImageLinkWithFiles
  }) => void
  editAd: (payload: {
    index: number
    ad: AdImageLinkWithFiles
    mode: AdModeType
  }) => void
  updatePercentages: () => void
  rebalancePercentages: (mode: AdModeType) => void
  loadApplicationCfg: () => Promise<void>
  updateApplicationCfg: (payload: { splashDuration: number }) => Promise<void>
  saveAllImg: () => Promise<void>
  reset: () => void
}

export const useAdsStore = create<AdsStore>((set, get) => ({
  ...new AdsState(),

  load: () => {
    set({ loading: true })
  },

  unload: () => {
    set({ loading: false })
  },

  setAdToDeleteIndex: (payload) => {
    set({
      currentAdIndex: payload.index,
      adMode: payload.mode,
      showDeleteDialog: true,
    })
  },

  setApplicationCfg: (applicationCfg) => {
    set({
      applicationCfg,
      drawerAds: applicationCfg.drawerAds,
      splashAds: applicationCfg.splashAds,
      splashDuration: applicationCfg.splashDuration,
      isDrawerAdsPercentageValid:
        !applicationCfg.features.showDrawerBottomAdvertisement ||
        isAdsPercentageValid(applicationCfg.features, applicationCfg.drawerAds),
      isSplashAdsPercentageValid:
        !applicationCfg.features.displaySplashAdvertisement ||
        isAdsPercentageValid(applicationCfg.features, applicationCfg.splashAds),
    })
  },

  setDrawerAdImage: (payload) => {
    set((state) => {
      const drawerAds = [...state.drawerAds]
      drawerAds[payload.index] = {
        ...drawerAds[payload.index],
        croppedImage: undefined,
        image: payload.filename,
      }
      return {
        drawerAds,
        isDrawerAdsPercentageValid: isAdsPercentageValid(
          state.applicationCfg?.features,
          drawerAds,
        ),
      }
    })
  },

  setSplashAdImage: (payload) => {
    set((state) => {
      const splashAds = [...state.splashAds]
      splashAds[payload.index] = {
        ...splashAds[payload.index],
        croppedImage: undefined,
        image: payload.filename,
      }
      return {
        splashAds,
        isSplashAdsPercentageValid: isAdsPercentageValid(
          state.applicationCfg?.features,
          splashAds,
        ),
      }
    })
  },

  setDrawerAdVideo: (payload) => {
    set((state) => {
      const drawerAds = [...state.drawerAds]
      drawerAds[payload.index] = {
        ...drawerAds[payload.index],
        videoFile: undefined,
        videoFilename: payload.filename,
      }
      return {
        drawerAds,
        isDrawerAdsPercentageValid: isAdsPercentageValid(
          state.applicationCfg?.features,
          drawerAds,
        ),
      }
    })
  },

  setSplashAdVideo: (payload) => {
    set((state) => {
      const splashAds = [...state.splashAds]
      splashAds[payload.index] = {
        ...splashAds[payload.index],
        videoFile: undefined,
        videoFilename: payload.filename,
      }
      return {
        splashAds,
        isSplashAdsPercentageValid: isAdsPercentageValid(
          state.applicationCfg?.features,
          splashAds,
        ),
      }
    })
  },

  deleteAd: () => {
    const state = get()
    if (state.currentAdIndex == null) {
      SnackbarService.error(
        'Error : Deletion of Ad impossible because currentAdIndex is undefined',
      )
      return
    }
    if (state.adMode === 'DRAWER') {
      const drawerAds = state.drawerAds.filter(
        (_, i) => i !== state.currentAdIndex,
      )
      set({
        drawerAds,
        isDrawerAdsPercentageValid: isAdsPercentageValid(
          state.applicationCfg?.features,
          drawerAds,
        ),
        currentAdIndex: undefined,
        adMode: undefined,
        showDeleteDialog: false,
      })
    } else {
      const splashAds = state.splashAds.filter(
        (_, i) => i !== state.currentAdIndex,
      )
      set({
        splashAds,
        isSplashAdsPercentageValid: isAdsPercentageValid(
          state.applicationCfg?.features,
          splashAds,
        ),
        currentAdIndex: undefined,
        adMode: undefined,
        showDeleteDialog: false,
      })
    }
  },

  closeDeleteAd: () => {
    set({
      currentAdIndex: undefined,
      adMode: undefined,
      showDeleteDialog: false,
    })
  },

  addAd: (mode) => {
    set({
      adMode: mode,
      currentAd: undefined,
      currentAdIndex: undefined,
      showAdModal: true,
    })
  },

  closeAdModal: () => {
    set({
      adMode: undefined,
      currentAd: undefined,
      currentAdIndex: undefined,
      showAdModal: false,
    })
  },

  saveOrUpdateAd: (payload) => {
    set((state) => {
      if (state.adMode === 'DRAWER') {
        const drawerAds = addOrUpdateAd(payload, state.drawerAds)
        return {
          drawerAds,
          isDrawerAdsPercentageValid: isAdsPercentageValid(
            state.applicationCfg?.features,
            drawerAds,
          ),
          currentAdIndex: undefined,
          currentAd: undefined,
          adMode: undefined,
          showAdModal: false,
        }
      }
      const splashAds = addOrUpdateAd(payload, state.splashAds)
      return {
        splashAds,
        isSplashAdsPercentageValid: isAdsPercentageValid(
          state.applicationCfg?.features,
          splashAds,
        ),
        currentAdIndex: undefined,
        currentAd: undefined,
        adMode: undefined,
        showAdModal: false,
      }
    })
  },

  editAd: (payload) => {
    set({
      adMode: payload.mode,
      currentAdIndex: payload.index,
      currentAd: { ...payload.ad },
      showAdModal: true,
    })
  },

  updatePercentages: () => {
    set((state) => ({
      isDrawerAdsPercentageValid: isAdsPercentageValid(
        state.applicationCfg?.features,
        state.drawerAds,
      ),
      isSplashAdsPercentageValid: isAdsPercentageValid(
        state.applicationCfg?.features,
        state.splashAds,
      ),
    }))
  },

  rebalancePercentages: (mode) => {
    const state = get()
    const ads = mode === 'DRAWER' ? state.drawerAds : state.splashAds
    if (ads.length === 0) return
    const base = Math.floor(100 / ads.length)
    const remainder = 100 % ads.length
    const rebalanced = ads.map((ad, index) => ({
      ...ad,
      percentage: base + (index < remainder ? 1 : 0),
    }))
    if (mode === 'DRAWER') {
      set({
        drawerAds: rebalanced,
        isDrawerAdsPercentageValid: isAdsPercentageValid(
          state.applicationCfg?.features,
          rebalanced,
        ),
      })
    } else {
      set({
        splashAds: rebalanced,
        isSplashAdsPercentageValid: isAdsPercentageValid(
          state.applicationCfg?.features,
          rebalanced,
        ),
      })
    }
  },

  async loadApplicationCfg() {
    try {
      get().load()
      const applicationCfg = await ApplicationConfigService.get()
      get().setApplicationCfg(toApplicationCfgWithAdsFiles(applicationCfg))
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async updateApplicationCfg(payload: { splashDuration: number }) {
    try {
      get().load()
      await get().saveAllImg()
      const { applicationCfg } = get()
      if (applicationCfg) {
        const updatedCfg: ApplicationCfgWithAdsFiles = {
          ...applicationCfg,
          splashAds: get().splashAds,
          drawerAds: get().drawerAds,
          splashDuration: payload.splashDuration,
        }
        set({ applicationCfg: updatedCfg })
        await ApplicationConfigService.updateApplicationCfg(updatedCfg)
        SnackbarService.info(t('ADS.SAVE'))
        await get().loadApplicationCfg()
      } else {
        SnackbarService.error(
          'updateApplicationCfg in ads page failed: applicationCfg was not loaded',
        )
      }
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async saveAllImg() {
    const state = get()
    await Promise.all(
      [
        ...state.drawerAds.flatMap((ad, index) => {
          return [
            ad.croppedImage != null
              ? ({
                  index,
                  commitKey: `SET_DRAWER_AD_IMAGE`,
                  croppedImage: ad.croppedImage,
                } as const)
              : null,
            ad.videoFile
              ? ({
                  index,
                  commitKey: `SET_DRAWER_AD_VIDEO`,
                  videoFile: ad.videoFile,
                } as const)
              : ad.videoFilename != null
                ? ({
                    index,
                    commitKey: `SET_DRAWER_AD_VIDEO`,
                    videoFilename: ad.videoFilename,
                  } as const)
                : ({
                    index,
                    commitKey: `SET_DRAWER_AD_VIDEO`,
                    videoFilename: null,
                  } as const),
          ]
        }),
        ...state.splashAds.flatMap((ad, index) => {
          return [
            ad.croppedImage != null
              ? ({
                  index,
                  commitKey: `SET_SPLASH_AD_IMAGE`,
                  croppedImage: ad.croppedImage,
                } as const)
              : null,
            ad.videoFile
              ? ({
                  index,
                  commitKey: `SET_SPLASH_AD_VIDEO`,
                  videoFile: ad.videoFile,
                } as const)
              : ad.videoFilename != null
                ? ({
                    index,
                    commitKey: `SET_SPLASH_AD_VIDEO`,
                    videoFilename: ad.videoFilename,
                  } as const)
                : ({
                    index,
                    commitKey: `SET_SPLASH_AD_VIDEO`,
                    videoFilename: null,
                  } as const),
          ]
        }),
      ]
        .filter(isNotNullish)
        .map(async (a) => {
          const { filename } =
            a.croppedImage != null
              ? await FilesService.uploadImage({
                  image: a.croppedImage,
                  internalType:
                    a.commitKey === 'SET_SPLASH_AD_IMAGE'
                      ? InternalType.SPLASHSCREEN
                      : InternalType.PICTURE,
                })
              : a.videoFile
                ? await FilesService.uploadVideo(
                    a.videoFile,
                    a.commitKey === 'SET_DRAWER_AD_VIDEO' ? 4 : 9 / 16,
                  )
                : { filename: a.videoFilename }

          switch (a.commitKey) {
            case 'SET_DRAWER_AD_IMAGE':
              if (filename != null) {
                get().setDrawerAdImage({
                  filename,
                  index: a.index,
                })
              }
              break
            case 'SET_SPLASH_AD_IMAGE':
              get().setSplashAdImage({
                filename: filename ?? null,
                index: a.index,
              })
              break
            case 'SET_DRAWER_AD_VIDEO':
              get().setDrawerAdVideo({
                filename: filename ?? null,
                index: a.index,
              })
              break
            case 'SET_SPLASH_AD_VIDEO':
              get().setSplashAdVideo({
                filename: filename ?? null,
                index: a.index,
              })
              break
          }
        }),
    )
  },

  reset: () => {
    set({
      ...new AdsState(),
    })
  },
}))
